'use client';

import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { FaChevronDown, FaTelegram } from '../icons';
import { useHost } from '../../runtime/RuntimeContext';
import { useLanguage } from '../../hooks/useLanguage';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useLocalizedHref } from '../../hooks/useLocalizedHref';
import { languageSwitchTarget, useLanguageAlternatesStore } from '../../i18n/languageAlternates';
import { withLangPrefix } from '../../i18n/locale';
import { languageCodes, languageFlags, languages } from '../../stores/languageStore';
import { getLegalLinks } from '../../utils/legalLinks';
import { getBotUrl } from '../../utils/telegram';
import { isCsShotUzHost } from '../../runtime/host';
import { isClosedForHost } from '../../seo/indexability';
import { routeIndexedLanguages } from '../../seo/routeSeo';
import legalTranslations from '../../screens/HomeScreen/components/LegalSection.translations';
import translations from './DesktopSiteFooter.translations';
import { cn } from '../../utils/cn';

const footerLinkClass =
  'w-fit text-[15px] leading-6 text-white/65 no-underline transition-colors duration-200 hover:text-white focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-purple';

const Column: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <section className="min-w-0">
    <h2 className="mb-3 text-[16px] font-semibold leading-6 text-white">{title}</h2>
    <nav className="flex flex-col gap-1" aria-label={title}>{children}</nav>
  </section>
);

export const DesktopSiteFooter: FC = () => {
  const { t, lang } = useLocalTranslation(translations);
  const { t: legalT } = useLocalTranslation(legalTranslations);
  const host = useHost();
  const localize = useLocalizedHref();
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const { language, changeLanguage } = useLanguage();
  const languageAlternates = useLanguageAlternatesStore((state) => state.alternates);
  const legalLinks = getLegalLinks(host);
  const isUz = isCsShotUzHost(host);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [desktopViewport, setDesktopViewport] = useState(false);
  const languageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktopViewport(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!languageOpen) return;
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [languageOpen]);

  const internalLink = (href: string, label: string) => (
    <Link className={footerLinkClass} href={localize(href)} prefetch={false}>{label}</Link>
  );

  if (!desktopViewport) return null;

  return (
    <footer
      data-site-footer
      className="relative hidden w-full px-10 pb-8 pt-10 lg:block xl:px-12"
    >
      <div className="border-t border-white/20 pt-12">
        <div className="grid grid-cols-[1.15fr_0.9fr_0.9fr_1.15fr] gap-x-14 pb-9 xl:gap-x-20">
          <section className="min-w-0">
            <Link
              href={localize('/')}
              className="inline-block font-sans text-[31px] font-extrabold uppercase tracking-[0.18em] no-underline"
              style={{
                background: 'linear-gradient(to top, #7C3AED 0%, #A78BFA 50%, #00C8FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.45)) drop-shadow(0 0 10px rgba(0,200,255,0.25))',
              }}
            >SHOT</Link>
            <p className="mt-1 text-[15px] leading-6 text-white/65">{t('tagline')}</p>
            <a
              href={getBotUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex h-11 items-center gap-3 rounded-full border border-white/40 px-5 text-[15px] text-white/85 no-underline transition-colors hover:border-white/70 hover:bg-white/[0.05] hover:text-white"
            >
              <FaTelegram aria-hidden className="text-[20px] text-accent-purple" />
              <span>{t('telegram')}</span>
              <span aria-hidden className="ms-1 text-[19px] leading-none">↗</span>
            </a>
            {isUz && (
              <div className="mt-5 flex items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-white px-2 py-1"><img src="/paylogos/uzcard.png" alt="UzCard" className="h-4 w-auto" /></span>
                <span className="inline-flex items-center rounded-md bg-white px-2 py-1"><img src="/paylogos/humo.png" alt="Humo" className="h-4 w-auto" /></span>
              </div>
            )}
          </section>

          <Column title={t('play')}>
            {internalLink('/cases', t('caseCatalog'))}
            {internalLink('/case-battles', t('caseBattles'))}
            {internalLink('/games/rush-mid', t('rushMid'))}
            {internalLink('/upgrade', t('upgrade'))}
            {internalLink('/contracts', t('contracts'))}
          </Column>

          <Column title={t('help')}>
            {internalLink('/how-it-works', t('faq'))}
            {routeIndexedLanguages('/wiki')?.includes(lang) && internalLink('/wiki', legalT('wiki'))}
            {!isClosedForHost('/support', host) && internalLink('/support', legalT('support'))}
          </Column>

          <Column title={t('information')}>
            {routeIndexedLanguages('/free-skins')?.includes(lang) && internalLink('/free-skins', legalT('freeSkin'))}
            <a className={footerLinkClass} href={legalLinks.userAgreement} target="_blank" rel="noopener noreferrer">{legalT('userAgreement')}</a>
            <a className={footerLinkClass} href={legalLinks.privacyPolicy} target="_blank" rel="noopener noreferrer">{legalT('privacyPolicy')}</a>
            {!isClosedForHost('/refund-terms', host) && internalLink('/refund-terms', legalT('refundTerms'))}
            {!isClosedForHost('/digital-content', host) && internalLink('/digital-content', legalT('digitalContent'))}
            {isUz && internalLink('/contacts', legalT('contacts'))}
          </Column>
        </div>

        <div className="flex min-h-16 items-center justify-between border-t border-white/20 pt-6">
          <p className="text-[13px] text-white/65">© {new Date().getFullYear()} SHOT. {t('copyright')}</p>
          <div ref={languageRef} className="relative">
            <button
              type="button"
              aria-expanded={languageOpen}
              aria-haspopup="menu"
              onClick={() => setLanguageOpen((open) => !open)}
              className="flex items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-2 text-[13px] text-white/80 transition-colors hover:bg-white/[0.05] hover:text-white"
            >
              <span className="text-base leading-none">{languageFlags[language]}</span>
              <span>{languages[language]}</span>
              <FaChevronDown aria-hidden className={cn('text-[9px] text-white/40 transition-transform', languageOpen && 'rotate-180')} />
            </button>
            <div
              role="menu"
              className={cn(
                'absolute bottom-full end-0 z-50 mb-2 min-w-[170px] overflow-hidden rounded-xl border border-white/[0.1] bg-[#0c0f16] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.7)]',
                !languageOpen && 'hidden',
              )}
            >
              {languageCodes.map((code) => (
                <a
                  key={code}
                  role="menuitem"
                  href={languageSwitchTarget(code, languageAlternates, withLangPrefix(code, pathname) + (search ? `?${search}` : ''))}
                  hrefLang={code}
                  className={cn('flex w-full items-center gap-2 px-3 py-2 text-xs no-underline transition-colors', code === language ? 'bg-white/[0.06] text-white' : 'text-white/60 hover:bg-white/[0.03] hover:text-white')}
                  onClick={(event) => { event.preventDefault(); changeLanguage(code); setLanguageOpen(false); }}
                >
                  <span>{languageFlags[code]}</span><span>{languages[code]}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
